// Jun-2026 — Phase 3C: Project Stock tab — read-only view over locked
// Project GRN inventory batches. Standard Issue / Transfer / Adjust / Scrap
// actions do NOT exist here by design (owner boundary).
// PMC-A (Jun-2026): eligible users additionally get a Consume action and a
// Consumption History sub-tab (Project Material Consumption — completely
// SEPARATE from Issue Voucher).
import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { RefreshCw, Lock, X, FileSpreadsheet, Eye, Undo2, Hammer, ArrowRightLeft, FileEdit } from "lucide-react";
import { ConsumeModal, ConsumptionHistory } from "@/components/ProjectConsumptionTab";
import { ProjectStockTransfers } from "@/components/ProjectStockTransfers";
import { ProjectOpeningStockDialog } from "@/components/ProjectOpeningStockDialog";
import GrnAmendModal from "@/components/GrnAmendModal";
import { useAuth } from "@/contexts/AuthContext";

const inputCls = "border border-slate-300 rounded px-2 py-1.5 text-xs";

function MovementsModal({ project, row, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/projects/${project.id}/stock/${row.batch_id}/movements`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load movements"));
  }, [project.id, row.batch_id]);
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center overflow-y-auto py-10" data-testid="stock-movements-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <div className="text-sm font-bold text-slate-900 font-mono">{row.batch_code}</div>
          <button data-testid="stock-movements-close" onClick={onClose} className="text-slate-500 hover:text-slate-800"><X size={16} /></button>
        </div>
        <div className="p-5 text-xs space-y-3">
          <div className={`inline-flex items-center gap-1 font-bold ${row.reversed ? "text-orange-700" : "text-emerald-700"}`}>
            {row.reversed ? <Undo2 size={12} /> : <Lock size={12} />} {row.lock_status}
          </div>
          {row.reversed && (
            <div className="text-orange-700 text-[11px]" data-testid="stock-movements-reversal-info">
              Reversed by {row.reversed_by} on {(row.reversed_at || "").slice(0, 10)} — {row.reversal_reason}
            </div>
          )}
          {!data ? <div className="text-slate-400">Loading…</div> : (
            <>
              <div className="text-[10px] uppercase font-bold text-slate-400">Stock Entries</div>
              {(data.entries || []).map((e, i) => (
                <div key={i} className="border-t border-slate-100 py-1.5">
                  <b>{e.entry_type}</b> · {e.quantity} {e.unit} · {e.entry_date}
                  <div className="text-slate-500">{e.remarks}</div>
                </div>
              ))}
              <div className="text-[10px] uppercase font-bold text-slate-400 mt-2">Warehouse Movements</div>
              {(data.movements || []).length === 0 && <div className="text-slate-400">None</div>}
              {(data.movements || []).map((m, i) => (
                <div key={i} className="border-t border-slate-100 py-1.5">
                  <b className="font-mono">{m.movement_no}</b> · {m.movement_type} · {m.quantity} {m.unit} → {m.to_warehouse_name}
                  <span className="text-slate-400"> · ref {m.reference_no}</span>
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function ProjectStockTab({ project }) {
  const pid = project.id;
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState({});
  const [valuesVisible, setValuesVisible] = useState(false);
  const [valueNote, setValueNote] = useState("");
  const [canConsume, setCanConsume] = useState(false);
  const [view, setView] = useState("stock");         // stock | history
  const [page, setPage] = useState(1);
  const [flt, setFlt] = useState({ q: "", date_from: "", date_to: "", po_id: "", grn_id: "" });
  const [drill, setDrill] = useState(null);
  const [consume, setConsume] = useState(null);
  // Jun-2026 (owner-authorized) — Amend the source GRN from the stock page
  // (controlled correction instead of a full reversal).
  const [amendGrn, setAmendGrn] = useState(null);
  // Jun-2026 — GRN-less opening stock (previous-ERP carry-in) bulk entry.
  const [openingStockOpen, setOpeningStockOpen] = useState(false);
  const { hasPerm } = useAuth();
  const canAmendGrn = hasPerm("project_grn", "edit");
  const canOpeningStock = hasPerm("project_opening_stock", "create");
  const limit = 25;

  // Shared row actions — desktop table cell AND mobile card (m = testid infix).
  const stockActions = (r, m = "") => (
    <>
      {canConsume && !r.reversed && r.available_qty > 0 && (
        <button data-testid={`stock-consume-${m}${r.batch_code}`} onClick={() => setConsume(r)}
          className="text-emerald-700 hover:underline inline-flex items-center gap-1 mr-2 font-bold">
          <Hammer size={12} /> Consume
        </button>
      )}
      {canAmendGrn && !r.reversed && !r.transferred_in && r.grn_id && (
        r.grn_amendment_pending ? (
          <span data-testid={`stock-amd-pending-${m}${r.batch_code}`}
            className="text-[9px] uppercase tracking-wider font-bold px-1 py-0.5 bg-amber-100 text-amber-800 mr-2 whitespace-nowrap"
            title="An amendment on the source GRN is pending approval">AMD Pending</span>
        ) : (
          <button data-testid={`stock-amend-${m}${r.batch_code}`} onClick={() => setAmendGrn(r.grn_id)}
            className="text-[#F97316] hover:underline inline-flex items-center gap-1 mr-2 font-bold"
            title="Correct the source GRN quantities (goes through the Approval Matrix)">
            <FileEdit size={12} /> Amend GRN
          </button>
        )
      )}
      <button data-testid={`stock-movements-${m}${r.batch_code}`} onClick={() => setDrill(r)}
        className="text-sky-700 hover:underline inline-flex items-center gap-1"><Eye size={12} /> Movements</button>
    </>
  );

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/projects/${pid}/stock`, { params: {
        page, limit,
        q: flt.q || undefined, date_from: flt.date_from || undefined,
        date_to: flt.date_to || undefined, po_id: flt.po_id || undefined,
        grn_id: flt.grn_id || undefined } });
      setRows(r.data?.rows || []);
      setTotal(r.data?.total || 0);
      setSummary(r.data?.summary || {});
      setValuesVisible(!!r.data?.values_visible);
      setValueNote(r.data?.value_note || "");
      setCanConsume(!!r.data?.can_consume);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load Project Stock");
    }
  }, [pid, page, flt]);

  useEffect(() => { load(); }, [load]);

  const doExport = async () => {
    try {
      const r = await api.get(`/projects/${pid}/stock-export`, {
        responseType: "blob",
        params: { q: flt.q || undefined, date_from: flt.date_from || undefined,
          date_to: flt.date_to || undefined } });
      const url = URL.createObjectURL(r.data);
      const link = document.createElement("a");
      link.href = url; link.download = `Project_Stock_${project.project_code}.xlsx`; link.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) { toast.error(e?.response?.data?.detail || "Export failed (project_stock export permission required)"); }
  };

  const drillFromHistory = (line) => {
    const r = rows.find((x) => x.batch_id === line.batch_id);
    setDrill(r || { batch_id: line.batch_id, batch_code: line.batch_code, lock_status: "Locked — Project GRN", reversed: false });
  };

  return (
    <div className="space-y-3" data-testid="project-stock-tab">
      <div className="border border-emerald-200 bg-emerald-50/60 rounded p-3 flex items-start gap-2 text-xs" data-testid="project-stock-readonly-banner">
        <Lock size={14} className="text-emerald-700 mt-0.5 shrink-0" />
        <div>
          <b className="text-emerald-900">Locked Project Stock.</b>{" "}
          Received via Project GRN and isolated from standard Issue / Transfer / Adjustment / Scrap / Sales flows.
          {canConsume
            ? " Daily usage is recorded through Project Material Consumption (separate from Issue Voucher)."
            : " Material consumption is recorded by assigned project users with the PMC permission."}
          <div className="text-emerald-800 mt-0.5 italic" data-testid="pmc-reporting-disclosure">
            Project Material Consumption is operationally posted. Integration into Project P&L, Budget vs Actual,
            Project Health, Monthly Performance and Forecast will be completed through a separately reconciled reporting phase.
          </div>
          {valuesVisible && valueNote && <div className="text-emerald-800 mt-0.5 italic">{valueNote}</div>}
        </div>
      </div>

      <div className="flex items-center gap-1 text-xs" data-testid="stock-view-switch">
        <button data-testid="stock-view-stock-btn" onClick={() => setView("stock")}
          className={`px-3 py-1.5 rounded font-bold border ${view === "stock" ? "bg-emerald-700 text-white border-emerald-700" : "border-slate-300 text-slate-600 hover:bg-slate-50"}`}>
          Stock
        </button>
        <button data-testid="stock-view-history-btn" onClick={() => setView("history")}
          className={`px-3 py-1.5 rounded font-bold border ${view === "history" ? "bg-emerald-700 text-white border-emerald-700" : "border-slate-300 text-slate-600 hover:bg-slate-50"}`}>
          Consumption History
        </button>
        <button data-testid="stock-view-transfers-btn" onClick={() => setView("transfers")}
          className={`px-3 py-1.5 rounded font-bold border inline-flex items-center gap-1 ${view === "transfers" ? "bg-emerald-700 text-white border-emerald-700" : "border-slate-300 text-slate-600 hover:bg-slate-50"}`}>
          <ArrowRightLeft size={12} /> Transfers
        </button>
      </div>

      {view === "transfers" ? (
        <ProjectStockTransfers project={project} onStockChanged={load} />
      ) : view === "history" ? (
        <ConsumptionHistory project={project} canConsume={canConsume} onDrill={drillFromHistory} />
      ) : (
      <>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <input data-testid="stock-search-input" className={`${inputCls} w-full sm:w-52`} placeholder="Search item / batch / PO / GRN…"
            value={flt.q} onChange={(e) => { setFlt({ ...flt, q: e.target.value }); setPage(1); }} />
          <input data-testid="stock-date-from" type="date" className={inputCls} value={flt.date_from}
            onChange={(e) => { setFlt({ ...flt, date_from: e.target.value }); setPage(1); }} />
          <input data-testid="stock-date-to" type="date" className={inputCls} value={flt.date_to}
            onChange={(e) => { setFlt({ ...flt, date_to: e.target.value }); setPage(1); }} />
          <button data-testid="stock-refresh-btn" onClick={load} className="p-1.5 text-slate-500 hover:text-slate-800"><RefreshCw size={14} /></button>
        </div>
        {canOpeningStock && (
          <button data-testid="opening-stock-btn" onClick={() => setOpeningStockOpen(true)}
            className="inline-flex items-center gap-1 px-3 py-1.5 mr-2 text-xs font-bold text-white bg-emerald-700 rounded hover:bg-emerald-800"
            title="Bulk add open stocks from the previous ERP — no GRN needed">
            + Opening Stock
          </button>
        )}
        <button data-testid="stock-export-btn" onClick={doExport}
          className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold text-emerald-800 border border-emerald-300 rounded hover:bg-emerald-50">
          <FileSpreadsheet size={13} /> Export Excel
        </button>
      </div>

      <div className="flex gap-3 text-xs" data-testid="stock-summary-chips">
        <span className="bg-slate-100 border border-slate-200 rounded px-2 py-1">Batches: <b>{summary.total_batches ?? total}</b></span>
        {valuesVisible && summary.total_available_qty !== undefined && (
          <span className="bg-slate-100 border border-slate-200 rounded px-2 py-1">Available Qty: <b>{summary.total_available_qty}</b></span>
        )}
        {valuesVisible && summary.total_provisional_value !== undefined && (
          <span className="bg-slate-100 border border-slate-200 rounded px-2 py-1" data-testid="stock-total-value">Provisional Value: <b>₹{Number(summary.total_provisional_value).toLocaleString("en-IN")}</b></span>
        )}
      </div>

      <div className="hidden md:block border border-slate-200 rounded overflow-x-auto">
        <table className="w-full min-w-[900px] text-xs" data-testid="project-stock-table">
          <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
            <tr>
              <th className="px-3 py-2 text-left">Item</th>
              <th className="px-3 py-2 text-left">UOM</th>
              <th className="px-3 py-2 text-left">PO</th>
              <th className="px-3 py-2 text-left">GRN</th>
              <th className="px-3 py-2 text-left">Vendor</th>
              <th className="px-3 py-2 text-right">Accepted</th>
              <th className="px-3 py-2 text-right">Rejected</th>
              <th className="px-3 py-2 text-right">Damaged</th>
              <th className="px-3 py-2 text-right">Consumed</th>
              <th className="px-3 py-2 text-right">Available</th>
              <th className="px-3 py-2 text-left">Location</th>
              {valuesVisible && <th className="px-3 py-2 text-right">Prov. Rate</th>}
              {valuesVisible && <th className="px-3 py-2 text-right">Prov. Value</th>}
              <th className="px-3 py-2 text-left">Lock</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={16} className="px-3 py-8 text-center text-slate-400" data-testid="project-stock-empty">
                No locked Project Stock yet — stock appears here after a Project GRN is approved.
              </td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.batch_id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`stock-row-${r.batch_code}`}>
                <td className="px-3 py-2">{r.item_name} <span className="text-slate-400 font-mono">{r.item_code}</span></td>
                <td className="px-3 py-2">{r.uom}</td>
                <td className="px-3 py-2 font-mono">{r.po_number}</td>
                <td className="px-3 py-2 font-mono">
                  {r.transferred_in ? (
                    <span className="text-sky-800" data-testid={`stock-transferred-in-${r.batch_code}`}>
                      {r.origin_transfer_number}
                      <div className="text-[10px] text-sky-600 font-sans font-bold">Transferred in ← {r.origin_project_code}</div>
                    </span>
                  ) : (<>{r.grn_number}<div className="text-slate-400">{r.grn_date}</div></>)}
                </td>
                <td className="px-3 py-2">{r.vendor_name}</td>
                <td className="px-3 py-2 text-right">{r.accepted_qty}</td>
                <td className="px-3 py-2 text-right">{r.rejected_qty}</td>
                <td className="px-3 py-2 text-right">{r.damaged_qty}</td>
                <td className="px-3 py-2 text-right" data-testid={`stock-consumed-${r.batch_code}`}>{r.consumed_qty ?? 0}</td>
                <td className="px-3 py-2 text-right font-bold">{r.available_qty}
                  {r.reserved_qty > 0 && (
                    <div className="text-[10px] text-amber-600 font-normal" data-testid={`stock-reserved-${r.batch_code}`}>
                      {r.reserved_qty} reserved for transfer
                    </div>
                  )}
                  {r.transferred_out_qty > 0 && (
                    <div className="text-[10px] text-sky-600 font-normal">{r.transferred_out_qty} transferred out</div>
                  )}
                </td>
                <td className="px-3 py-2">{r.storage_location}</td>
                {valuesVisible && <td className="px-3 py-2 text-right">{r.provisional_rate}</td>}
                {valuesVisible && <td className="px-3 py-2 text-right">{r.provisional_value}</td>}
                <td className="px-3 py-2">
                  {r.reversed ? (
                    <span className="inline-flex items-center gap-1 text-orange-700 font-bold" data-testid={`stock-reversed-${r.batch_code}`} title={r.reversal_reason}>
                      <Undo2 size={11} /> Reversed
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-emerald-700 font-bold" data-testid={`stock-lock-${r.batch_code}`}>
                      <Lock size={11} /> Locked
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 text-right whitespace-nowrap">{stockActions(r)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile: stacked stock cards */}
      <div className="md:hidden border border-slate-200 rounded divide-y divide-slate-100" data-testid="project-stock-cards">
        {rows.length === 0 && (
          <div className="px-3 py-6 text-center text-slate-400 text-xs">
            No locked Project Stock yet — stock appears here after a Project GRN is approved.
          </div>
        )}
        {rows.map((r) => (
          <div key={r.batch_id} className="p-3 space-y-1.5" data-testid={`stock-card-${r.batch_code}`}>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-bold text-slate-900 break-words">{r.item_name}</span>
              <span className="text-[10px] font-mono text-slate-400">{r.item_code}</span>
              <span className="ml-auto shrink-0">{r.reversed ? (
                <span className="inline-flex items-center gap-1 text-orange-700 font-bold text-[10px]" title={r.reversal_reason}><Undo2 size={10} /> Reversed</span>
              ) : (
                <span className="inline-flex items-center gap-1 text-emerald-700 font-bold text-[10px]"><Lock size={10} /> Locked</span>
              )}</span>
            </div>
            <div className="text-[10px] text-slate-500 break-words">
              {r.transferred_in
                ? <>Transferred in ← {r.origin_project_code} · <span className="font-mono">{r.origin_transfer_number}</span></>
                : <><span className="font-mono">{r.po_number}</span>{r.po_number ? " · " : ""}<span className="font-mono">{r.grn_number}</span> {r.grn_date}</>}
              {r.vendor_name ? ` · ${r.vendor_name}` : ""}
            </div>
            <div className="grid grid-cols-3 gap-x-2 gap-y-0.5 text-[11px]">
              <span className="text-slate-500">Accepted: <b>{r.accepted_qty}</b></span>
              <span className="text-slate-500">Rejected: <b>{r.rejected_qty}</b></span>
              <span className="text-slate-500">Damaged: <b>{r.damaged_qty}</b></span>
              <span className="text-slate-500">Consumed: <b>{r.consumed_qty ?? 0}</b></span>
              <span className="text-slate-500 col-span-2">Available: <b className="text-slate-900">{r.available_qty}</b> {r.uom}</span>
            </div>
            {(r.reserved_qty > 0 || r.transferred_out_qty > 0) && (
              <div className="text-[10px]">
                {r.reserved_qty > 0 && <span className="text-amber-600">{r.reserved_qty} reserved for transfer </span>}
                {r.transferred_out_qty > 0 && <span className="text-sky-600">{r.transferred_out_qty} transferred out</span>}
              </div>
            )}
            <div className="text-[10px] text-slate-500 flex flex-wrap gap-x-3">
              {r.storage_location && <span>Loc: {r.storage_location}</span>}
              {valuesVisible && <span>Rate: {r.provisional_rate}</span>}
              {valuesVisible && <span>Value: {r.provisional_value}</span>}
            </div>
            <div className="flex flex-wrap items-center gap-y-1 pt-0.5 text-xs">{stockActions(r, "m-")}</div>
          </div>
        ))}
      </div>
      {total > limit && (
        <div className="flex items-center justify-end gap-2 text-xs">
          <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="px-2 py-1 border rounded disabled:opacity-40">Prev</button>
          <span>Page {page} / {Math.ceil(total / limit)}</span>
          <button disabled={page >= Math.ceil(total / limit)} onClick={() => setPage(page + 1)} className="px-2 py-1 border rounded disabled:opacity-40">Next</button>
        </div>
      )}
      </>
      )}

      {drill && <MovementsModal project={project} row={drill} onClose={() => setDrill(null)} />}
      {openingStockOpen && (
        <ProjectOpeningStockDialog project={project}
          onClose={() => setOpeningStockOpen(false)} onSaved={load} />
      )}
      {consume && <ConsumeModal project={project} row={consume} onClose={() => setConsume(null)} onPosted={load} />}
      {amendGrn && (
        <GrnAmendModal project={project} grnId={amendGrn}
          onClose={() => setAmendGrn(null)}
          onDone={() => { setAmendGrn(null); load(); }} />
      )}
    </div>
  );
}
